import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'

type InvitationPreview = {
  email: string
  fullName: string
  role: 'admin' | 'manager' | 'employee'
  organizationName: string
  expiresAt: string
  status: 'pending' | 'accepted' | 'declined' | 'expired'
  expired: boolean
  deviceSetup?: {
    required: boolean
    osType: string
    installerUrl: string
  }
}

type PageState =
  | 'loading'
  | 'ready'
  | 'missingToken'
  | 'expired'
  | 'alreadyAccepted'
  | 'error'
  | 'accepted'
  | 'deviceSetup'

export const AcceptInvitePage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [pageState, setPageState] = useState<PageState>('loading')
  const [preview, setPreview] = useState<InvitationPreview | null>(null)
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    // Ensure any existing user session is cleared to avoid cross-account issues
    supabase.auth.signOut().catch(() => {
      // ignore sign out errors here
    })
  }, [])

  useEffect(() => {
    if (!token) {
      setPageState('missingToken')
      return
    }

    const loadInvitation = async () => {
      setPageState('loading')
      setSubmitError(null)

      const { data, error } = await supabase.functions.invoke<{ data?: InvitationPreview; error?: string }>('accept-invite', {
        body: { intent: 'preview', token }
      })

      if (error) {
        console.error('AcceptInvitePage: failed to load invitation', error)
        const status = (error as { status?: number }).status
        if (status === 409) {
          setPageState('alreadyAccepted')
        } else if (status === 410) {
          setPageState('expired')
        } else if (status === 404) {
          setPageState('missingToken')
        } else {
          setPageState('error')
        }
        return
      }

      if (!data?.data) {
        setPageState('error')
        return
      }

      setPreview(data.data)
      setFullName(data.data.fullName)

      // Debug: Log device setup info
      console.log('AcceptInvitePage: Preview data', data.data)
      console.log('AcceptInvitePage: Device setup?', data.data.deviceSetup)

      if (data.data.status === 'accepted') {
        setPageState('alreadyAccepted')
      } else if (data.data.expired) {
        setPageState('expired')
      } else {
        setPageState('ready')
      }
    }

    loadInvitation()
  }, [token])

  const canSubmit = useMemo(() => {
    if (!password || !confirmPassword || password !== confirmPassword) {
      return false
    }
    return password.length >= 8
  }, [password, confirmPassword])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!token || !preview || !canSubmit) {
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    const { data, error } = await supabase.functions.invoke<{
      success?: boolean
      email?: string
      error?: string
      deviceSetup?: {
        required: boolean
        osType: string
        installerUrl: string
      }
    }>('accept-invite', {
      body: {
        intent: 'accept',
        token,
        password,
        fullName
      }
    })

    if (error || data?.error) {
      console.error('AcceptInvitePage: failed to accept invitation', error || data?.error)
      const message = data?.error || (error as { message?: string })?.message || 'Failed to activate your account. Please try again.'
      setSubmitError(message)
      setIsSubmitting(false)
      return
    }

    // Debug: Log accept response
    console.log('AcceptInvitePage: Accept response', data)
    console.log('AcceptInvitePage: Device setup in response?', data?.deviceSetup)

    const email = data?.email || preview.email

    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password
    })

    if (signInError) {
      console.error('AcceptInvitePage: automatic sign-in failed', signInError)
      setSubmitError('Account activated, but automatic sign in failed. Please log in manually.')
      setIsSubmitting(false)
      setPageState('accepted')
      return
    }

    setPageState('accepted')
    setIsSubmitting(false)

    // Update preview with device setup from accept response
    if (data?.deviceSetup) {
      setPreview(prev => prev ? { ...prev, deviceSetup: data.deviceSetup } : prev)
    }

    // If device setup is required, show device setup page or redirect to agent download
    const hasDeviceSetup = data?.deviceSetup?.required || preview.deviceSetup?.required

    console.log('AcceptInvitePage: Has device setup?', hasDeviceSetup, {
      fromResponse: data?.deviceSetup,
      fromPreview: preview.deviceSetup,
      role: preview.role
    })

    if (hasDeviceSetup) {
      // Both managers and employees go to agent download page to select their OS/architecture
      setTimeout(() => {
        navigate('/agent/download', { replace: true })
      }, 1500) // Show success message briefly first
    } else {
      navigate('/dashboard', { replace: true })
    }
  }

  const renderContent = () => {
    switch (pageState) {
      case 'loading':
        return (
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="h-10 w-10 animate-spin rounded-full border-3 border-gray-400 border-t-transparent border-r-transparent" />
            <p className="text-sm text-gray-600">Checking your invitation…</p>
          </div>
        )
      case 'missingToken':
        return (
          <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center">
            <h1 className="text-lg font-semibold text-red-600">Invitation link is invalid</h1>
            <p className="mt-2 text-sm text-red-500">Please contact your manager for a new invitation.</p>
          </div>
        )
      case 'expired':
        return (
          <div className="rounded-3xl border border-amber-200 bg-amber-50 p-8 text-center">
            <h1 className="text-lg font-semibold text-amber-700">Invitation expired</h1>
            <p className="mt-2 text-sm text-amber-600">
              This invitation has expired. Please ask your manager to send a new invite.
            </p>
          </div>
        )
      case 'alreadyAccepted':
        return (
          <div className="rounded-3xl border border-green-200 bg-green-50 p-8 text-center">
            <h1 className="text-lg font-semibold text-green-700">Invitation already used</h1>
            <p className="mt-2 text-sm text-green-600">
              This invitation has already been accepted. You can sign in using your credentials.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 inline-flex items-center rounded-full bg-gray-900 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800"
            >
              Go to Login
            </button>
          </div>
        )
      case 'error':
        return (
          <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center">
            <h1 className="text-lg font-semibold text-red-600">We couldn’t verify your invitation</h1>
            <p className="mt-2 text-sm text-red-500">
              Please check your link or contact the Helpdesk 411 support team for help.
            </p>
          </div>
        )
      case 'accepted':
        return (
          <div className="rounded-3xl border border-green-200 bg-green-50 p-8 text-center">
            <h1 className="text-lg font-semibold text-green-700">Welcome to Helpdesk 411!</h1>
            <p className="mt-2 text-sm text-green-600">
              Your account is ready. Redirecting you to your dashboard…
            </p>
          </div>
        )
      case 'ready':
        if (!preview) return null
        return (
          <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-xl">
            <div className="mb-6 text-center">
              <p className="text-sm uppercase tracking-wide text-gray-500">Welcome to</p>
              <h1 className="mt-1 text-2xl font-semibold text-gray-900">{preview.organizationName}</h1>
              <p className="mt-2 text-sm text-gray-600">
                You’re joining the team as a <span className="font-medium capitalize text-gray-900">{preview.role}</span>.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="full-name">Full name</label>
                <input
                  id="full-name"
                  type="text"
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  className="w-full rounded-full border border-gray-300 px-4 py-3 text-sm text-gray-900 focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="password">Create password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={8}
                  className="w-full rounded-full border border-gray-300 px-4 py-3 text-sm text-gray-900 focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder="At least 8 characters"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="confirm-password">Confirm password</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  minLength={8}
                  className="w-full rounded-full border border-gray-300 px-4 py-3 text-sm text-gray-900 focus:border-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  placeholder="Repeat your password"
                  required
                />
              </div>

              {submitError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="w-full rounded-full bg-gray-900 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {isSubmitting ? 'Creating account…' : 'Join Workspace'}
              </button>
            </form>
            <p className="mt-6 text-center text-xs text-gray-500">
              Having trouble? Contact <a className="font-medium text-gray-900" href="mailto:support@helpdesk411.com">support@helpdesk411.com</a>
            </p>
          </div>
        )
      case 'deviceSetup':
        if (!preview?.deviceSetup) return null
        const osDisplayName = preview.deviceSetup.osType === 'mac' ? 'macOS' : preview.deviceSetup.osType === 'linux' ? 'Linux' : 'Windows'
        return (
          <div className="rounded-3xl border border-gray-200 bg-white p-8 shadow-xl">
            <div className="mb-6 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h1 className="text-2xl font-semibold text-gray-900">Download Device Agent</h1>
              <p className="mt-2 text-sm text-gray-600">
                Install the Kaseya agent on your {osDisplayName} device to enable remote IT support.
              </p>
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-6">
                <h3 className="mb-3 text-sm font-semibold text-blue-900">Installation Instructions:</h3>
                <ol className="space-y-2 text-sm text-blue-800">
                  <li className="flex gap-2">
                    <span className="font-semibold">1.</span>
                    <span>Click the download button below to get the installer</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold">2.</span>
                    <span>Run the downloaded file and follow the prompts</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold">3.</span>
                    <span>The agent will install automatically in the background</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-semibold">4.</span>
                    <span>Your device will appear in the dashboard within 15 minutes</span>
                  </li>
                </ol>
              </div>

              <a
                href={preview.deviceSetup.installerUrl}
                download
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gray-900 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download {osDisplayName} Installer
              </a>

              <button
                onClick={() => navigate('/dashboard', { replace: true })}
                className="w-full rounded-full border border-gray-300 bg-white px-6 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                Skip for Now (Install Later)
              </button>

              <p className="text-center text-xs text-gray-500">
                Need help? Contact your IT manager or{' '}
                <a className="font-medium text-gray-900" href="mailto:support@helpdesk411.com">
                  support@helpdesk411.com
                </a>
              </p>
            </div>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="mb-8 text-center">
          <img
            src="https://helpdesk411-assets.s3.amazonaws.com/logo-dark.png"
            alt="Helpdesk 411"
            className="mx-auto h-12"
          />
        </div>
        {renderContent()}
      </div>
    </div>
  )
}
